
import { debugLog } from "../utils/debugLogger";

export interface RuntimeConfig {
  polar: {
    orgSlug: string;
    productIdMonthly: string;
    productIdYearly: string;
  };
  supabase: {
    url: string;
    anonKey: string;
  };
}

let configCache: RuntimeConfig | null = null;

export const loadRuntimeConfig = async (): Promise<RuntimeConfig> => {
  if (configCache) return configCache;

  // 1. Try to fetch from API (Cloudflare Functions)
  try {
    debugLog("CONFIG", "Fetching runtime configuration...");
    const res = await fetch('/api/runtime-config');
    if (res.ok) {
        const data = await res.json();
        configCache = data as RuntimeConfig;
        debugLog("CONFIG", "Configuration loaded from API", { 
            polarSlug: configCache.polar.orgSlug,
            hasSupabaseUrl: !!configCache.supabase.url 
        });
        return configCache;
    } else {
        console.warn(`Config API returned ${res.status}. Falling back to build-time env.`);
    }
  } catch (error) {
    console.warn("Config API unreachable. Falling back to build-time env.");
  }

  // 2. Fallback to Build-Time / Local Env
  // We explicitly access import.meta.env.* properties so Vite can statically replace them.
  // Dynamic access (e.g. env[key]) often fails in Vite production builds.
  
  // Cast import.meta to any to avoid TS errors if types aren't set up
  const metaEnv = (import.meta as any).env || {};

  configCache = {
    polar: {
        orgSlug: metaEnv.VITE_POLAR_ORG_SLUG || '',
        productIdMonthly: metaEnv.VITE_POLAR_PRODUCT_ID_MONTHLY || '',
        productIdYearly: metaEnv.VITE_POLAR_PRODUCT_ID_YEARLY || '',
    },
    supabase: {
        url: metaEnv.VITE_SUPABASE_URL || '',
        anonKey: metaEnv.VITE_SUPABASE_ANON_KEY || '',
    }
  };

  debugLog("CONFIG", "Configuration loaded from ENV (Fallback)", { 
      polarSlug: configCache.polar.orgSlug,
      hasSupabaseUrl: !!configCache.supabase.url 
  });

  return configCache;
};

export const getRuntimeConfig = (): RuntimeConfig => {
  if (!configCache) {
    // Synchronous fallback if accessed early
    const metaEnv = (import.meta as any).env || {};
    return {
        polar: {
            orgSlug: metaEnv.VITE_POLAR_ORG_SLUG || '',
            productIdMonthly: metaEnv.VITE_POLAR_PRODUCT_ID_MONTHLY || '',
            productIdYearly: metaEnv.VITE_POLAR_PRODUCT_ID_YEARLY || '',
        },
        supabase: {
            url: metaEnv.VITE_SUPABASE_URL || '',
            anonKey: metaEnv.VITE_SUPABASE_ANON_KEY || '',
        }
    };
  }
  return configCache;
};
